/**
 * Ask Atlas — AI Q&A panel (doc 147 §8.4, §9 Phase 4).
 *
 * Presentational: the injected `onAsk` runs the SSE call and resolves with the
 * accumulated answer + grounding result. The view shows the answer and warns
 * when the model cited entity names that are not in the manifest.
 */
import { useEffect, useRef, useState } from 'react';
import type { AskAtlasResult } from '../state/atlasApiTypes.js';
import { Icon } from '../components/Icon.js';
import { ErrorState } from '../components/ErrorState.js';
import { AiMarkdown } from '../components/AiMarkdown.js';
import { buildSuggestions, type AskAtlasContext } from './askAtlasContext.js';

export interface AskAtlasViewProps {
  onAsk: (question: string) => Promise<AskAtlasResult>;
  /** New: screen context for building context-aware suggestions.
   *  When set, example prompts vary by screen kind (entity/sandbox/etc.).
   *  Deprecated: use screenContext instead of focusEntityId. */
  screenContext?: AskAtlasContext;
  /** Legacy: when set without screenContext, wraps the entity id into
   *  a screenContext of kind 'entity'. Kept for backward compatibility. */
  focusEntityId?: string;
}

interface Exchange {
  question: string;
  result: AskAtlasResult | null;
  error: boolean;
}

/**
 * @deprecated Use buildSuggestions with AskAtlasContext instead.
 * Kept for backward compatibility with existing tests.
 */
export function buildContextualPrompts(entityId: string): string[] {
  return buildSuggestions({ kind: 'entity', entityId });
}

export function AskAtlasView({ onAsk, screenContext, focusEntityId }: AskAtlasViewProps): React.JSX.Element {
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [exchanges]);

  const ask = async (q?: string): Promise<void> => {
    const text = (q ?? question).trim();
    if (!text || busy) return;
    setBusy(true);
    setQuestion('');
    const pending: Exchange = { question: text, result: null, error: false };
    setExchanges((prev) => [...prev, pending]);
    try {
      const result = await onAsk(text);
      setExchanges((prev) =>
        prev.map((e) => (e === pending ? { ...e, result } : e)),
      );
    } catch {
      setExchanges((prev) =>
        prev.map((e) => (e === pending ? { ...e, error: true } : e)),
      );
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  const empty = exchanges.length === 0;
  // Derive context: screenContext takes precedence, then fall back to focusEntityId,
  // then null (generic prompts).
  const context: AskAtlasContext = screenContext
    ?? (focusEntityId ? { kind: 'entity', entityId: focusEntityId } : null);
  const examplePrompts = buildSuggestions(context);

  // Compute context chip label and icon for rendering.
  const contextLabel = (() => {
    if (context === null) return null;
    switch (context.kind) {
      case 'entity':
        return context.entityId;
      case 'sandbox':
        return context.sandboxId ? `Sandbox: ${context.sandboxId}` : 'Sandbox';
      case 'simulate':
        return context.focusEntityId ? `Scenario: ${context.focusEntityId}` : 'Scenario';
      case 'catalog':
        return context.query ? `Search: ${context.query}` : 'Catalog';
      case 'catalog-domain':
        return context.domainId;
      case 'dashboard':
        return 'Dashboard';
    }
  })();

  return (
    <div className="ask-root">
      {/* Header */}
      <header className="ask-header">
        <span className="material-symbols-rounded ask-header-icon">auto_awesome</span>
        <div>
          <h1 className="ask-header-title">Ask Atlas</h1>
          <p className="ask-header-sub">
            Answers are grounded in the manifest — fabricated entities are flagged.
          </p>
        </div>
      </header>

      {/* Thread */}
      <div className="ask-thread" ref={threadRef}>
        {empty && (
          <div className="ask-empty">
            {contextLabel && (
              <div className="ask-context-chip">
                <Icon name="filter_center_focus" size="xs" />
                <span>
                  Asking about <strong>{contextLabel}</strong>
                </span>
              </div>
            )}
            <span className="material-symbols-rounded ask-empty-icon">chat_bubble_outline</span>
            <p className="ask-empty-label">Ask Atlas anything about your data model</p>
            <div className="ask-examples">
              {examplePrompts.map((p) => (
                <button
                  key={p}
                  type="button"
                  className="ask-example-btn"
                  onClick={() => void ask(p)}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        {exchanges.map((ex, i) => (
          <div key={i} className="ask-exchange">
            {/* User bubble */}
            <div className="ask-bubble-row ask-bubble-row--user">
              <div className="ask-bubble ask-bubble--user">{ex.question}</div>
            </div>

            {/* AI bubble */}
            <div className="ask-bubble-row ask-bubble-row--ai">
              <div className="ask-avatar">
                <span className="material-symbols-rounded">smart_toy</span>
              </div>
              <div className="ask-ai-content">
                {ex.result === null && !ex.error && (
                  <div className="ask-bubble ask-bubble--ai ask-bubble--thinking">
                    <span className="ask-thinking-dot" />
                    <span className="ask-thinking-dot" />
                    <span className="ask-thinking-dot" />
                  </div>
                )}
                {ex.error && (
                  <div className="ask-bubble ask-bubble--ai ask-bubble--error" role="alert">
                    <ErrorState
                      bare
                      variant="inline"
                      message="Ask Atlas is unavailable right now. Please try again."
                      onRetry={() => void ask(ex.question)}
                    />
                  </div>
                )}
                {ex.result && (
                  <>
                    <div className="ask-bubble ask-bubble--ai">
                      <AiMarkdown text={ex.result.answer} />
                    </div>

                    {/* Fabricated entities warning */}
                    {ex.result.fabricated.length > 0 && (
                      <div className="ask-fabricated" role="alert">
                        <Icon name="warning" size="xs" />
                        <span>
                          <strong>Unverified entities:</strong>{' '}
                          {ex.result.fabricated.join(', ')} — not found in manifest
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Input bar */}
      <div className="ask-input-bar">
        <textarea
          ref={inputRef}
          className="ask-input"
          aria-label="Ask Atlas a question"
          placeholder="e.g. what writes to Holding?"
          rows={1}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void ask();
            }
          }}
        />
        <button
          type="button"
          className="btn btn-primary ask-send-btn"
          onClick={() => void ask()}
          disabled={busy || !question.trim()}
          aria-label="Ask"
        >
          <span className="material-symbols-rounded" aria-hidden="true">send</span>
        </button>
        {busy && <span role="status" aria-label="Asking Atlas" className="spinner" />}
      </div>
    </div>
  );
}
